import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  alinearElementos,
  aplicarColoresMarca,
  colorDeMarca,
  COLOR_MARCA_PRIMARIO,
  COLOR_MARCA_SECUNDARIO,
  cuadrarProductos,
  distribuirElementos,
  LIENZOS,
  zPosterElement,
  type ColoresMarca,
  type Formato,
  type ModoAlineado,
  type PosterElement,
} from '@postmaker/poster';
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Lock,
  Minus,
  Plus,
  Redo2,
  Undo2,
} from 'lucide-react';
import { api, apiMultipart } from '../../lib/api.ts';

interface MarcaVista {
  id: string;
  nombre: string;
  logoUrl: string | null;
  colorPrimario: string | null;
  colorSecundario: string | null;
}
import { registrarFuentes } from '../../lib/fuentes.ts';
import { resueltoDeMuestra } from '../../lib/muestra.ts';
import { LienzoEditor } from '../../componentes/editor/LienzoEditor.tsx';
import { SelectorFuente } from '../../componentes/editor/SelectorFuente.tsx';
import { Aviso, Boton, BotonCirculo, Casilla, Chip } from '../../componentes/ui.tsx';

interface LienzoApi {
  formato: Formato;
  fondoColor: string | null;
  fondoAssetId: string | null;
  fondoUrl: string | null;
  elementos: unknown[];
}

interface PlantillaApi {
  id: string;
  nombre: string;
  marcaIds: string[];
  numProductos: number;
  estado: string;
  lienzos: LienzoApi[];
  /** assetId → URL firmada de los elementos `imagen`. */
  assets: Record<string, string>;
}

interface Fuente {
  id: string;
  familia: string;
  peso: number;
  italic: boolean;
  url: string | null;
}

const FORMATOS: Formato[] = ['R4_5', 'R1_1', 'R9_16'];

/** Nombres comerciales: el cliente elige "WhatsApp", no "1:1". */
const NOMBRES_FORMATO: Record<string, string> = {
  R1_1: 'WhatsApp 1:1',
  R4_5: 'Redes 4:5',
  R9_16: 'Historias 9:16',
};

const RESTRICCIONES_DEFECTO = {
  proporcionFija: false,
  dentroDelLienzo: true,
  tamanoMin: 2,
  tamanoMax: 100,
  rotable: false,
};
const ANCLA_DEFECTO = { h: 'centro', v: 'centro', escala: 'ninguna' } as const;

let contadorId = 0;
function idNuevo(prefijo: string): string {
  contadorId += 1;
  return `${prefijo}-${Date.now().toString(36)}${contadorId}`;
}

/** Editor de plantillas de la empresa (F4). Escritorio-primero (D37). */
export function PaginaEditorPlantilla() {
  const { plantillaId } = useParams({ strict: false }) as { plantillaId: string };
  const qc = useQueryClient();

  const [formato, setFormato] = useState<Formato>('R4_5');
  const [elementos, setElementos] = useState<PosterElement[]>([]);
  const [fondoColor, setFondoColor] = useState<string | null>('#ffffff');
  const [fondoAssetId, setFondoAssetId] = useState<string | null>(null);
  const [fondoUrl, setFondoUrl] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<string[]>([]);
  const [zoom, setZoom] = useState(0.42);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [sucio, setSucio] = useState(false);

  const historial = useRef<PosterElement[][]>([]);
  const rehacer = useRef<PosterElement[][]>([]);
  const fondoInputRef = useRef<HTMLInputElement>(null);
  const imagenInputRef = useRef<HTMLInputElement>(null);
  /** URLs de los assets de elementos imagen (los de la plantilla + los recién subidos). */
  const [assets, setAssets] = useState<Record<string, string>>({});
  /** Si hay un elemento imagen seleccionado, "subir" lo reemplaza; si no, crea uno nuevo. */
  const reemplazarImagenDe = useRef<string | null>(null);

  const plantilla = useQuery({
    queryKey: ['admin-plantilla', plantillaId],
    queryFn: () => api<PlantillaApi>(`/admin/plantillas/${plantillaId}`),
  });
  const fuentes = useQuery({
    queryKey: ['admin-fuentes'],
    queryFn: () => api<{ items: Fuente[] }>('/admin/fuentes'),
  });
  const fuenteDefecto = fuentes.data?.items[0]?.id ?? 'sistema';

  // Las fuentes de la empresa se cargan en el navegador: el preview usa la letra REAL.
  useEffect(() => {
    if (fuentes.data) {
      void registrarFuentes(
        fuentes.data.items.map((f) => ({
          fuenteId: f.id,
          familia: f.familia,
          url: f.url,
          peso: f.peso,
          italic: f.italic,
        })),
      );
    }
  }, [fuentes.data]);

  // Carga del lienzo del formato activo.
  useEffect(() => {
    const lienzo = plantilla.data?.lienzos.find((l) => l.formato === formato);
    const parseados = lienzo ? zPosterElement.array().safeParse(lienzo.elementos) : null;
    // D21: bounding box cuadrado para productos, aunque la plantilla venga vieja.
    setElementos(parseados?.success ? cuadrarProductos(parseados.data, LIENZOS[formato]) : []);
    setFondoColor(lienzo?.fondoColor ?? '#ffffff');
    setFondoAssetId(lienzo?.fondoAssetId ?? null);
    setFondoUrl(lienzo?.fondoUrl ?? null);
    setAssets((previos) => ({ ...previos, ...(plantilla.data?.assets ?? {}) }));
    setSeleccion([]);
    historial.current = [];
    rehacer.current = [];
    setSucio(false);
  }, [plantilla.data, formato]);

  /** Cambio en vivo (durante un gesto): sin historial. */
  const cambiarEnVivo = useCallback(
    (nuevos: PosterElement[]) => {
      setElementos(cuadrarProductos(nuevos, LIENZOS[formato]));
      setSucio(true);
    },
    [formato],
  );

  /** Apila un estado previo en el historial (fin de gesto o edición puntual). */
  const apilarHistorial = useCallback((previo: PosterElement[]) => {
    historial.current.push(previo);
    if (historial.current.length > 80) historial.current.shift();
    rehacer.current = [];
  }, []);

  /** Cambio atómico con historial (teclado, propiedades, agregar/borrar). */
  const cambiarElementos = useCallback(
    (nuevos: PosterElement[], commit: boolean) => {
      if (commit) apilarHistorial(elementos);
      setElementos(cuadrarProductos(nuevos, LIENZOS[formato]));
      setSucio(true);
    },
    [elementos, apilarHistorial, formato],
  );

  function deshacer() {
    const previo = historial.current.pop();
    if (previo) {
      rehacer.current.push(elementos);
      setElementos(previo);
      setSucio(true);
    }
  }

  function rehacerCambio() {
    const siguiente = rehacer.current.pop();
    if (siguiente) {
      historial.current.push(elementos);
      setElementos(siguiente);
      setSucio(true);
    }
  }

  const elemento = elementos.find((e) => e.id === seleccion[0]) ?? null;
  const idsSeleccion = new Set(seleccion);

  function alinearSeleccion(eje: ModoAlineado) {
    if (seleccion.length === 0) return;
    cambiarElementos(alinearElementos(elementos, idsSeleccion, eje), true);
  }

  function distribuirSeleccion(eje: 'horizontal' | 'vertical') {
    if (seleccion.length < 3) return;
    cambiarElementos(distribuirElementos(elementos, idsSeleccion, eje), true);
  }

  // Teclado: flechas 1/10, ⌫, ⌘Z, ⌘D, esc (§5.3).
  useEffect(() => {
    function alTeclear(ev: KeyboardEvent) {
      const objetivo = ev.target as HTMLElement;
      if (objetivo.tagName === 'INPUT' || objetivo.tagName === 'TEXTAREA' || objetivo.tagName === 'SELECT') return;

      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'z') {
        ev.preventDefault();
        if (ev.shiftKey) rehacerCambio();
        else deshacer();
        return;
      }
      if (!elemento) return;

      if ((ev.metaKey || ev.ctrlKey) && ev.key.toLowerCase() === 'd') {
        ev.preventDefault();
        const copia = {
          ...elemento,
          id: idNuevo(elemento.tipo),
          nombre: `${elemento.nombre} (copia)`,
          rect: { ...elemento.rect, x: Math.min(elemento.rect.x + 3, 90), y: Math.min(elemento.rect.y + 3, 90) },
        } as PosterElement;
        cambiarElementos([...elementos, copia], true);
        setSeleccion([copia.id]);
        return;
      }
      if (ev.key === 'Backspace' || ev.key === 'Delete') {
        ev.preventDefault();
        cambiarElementos(elementos.filter((e) => !idsSeleccion.has(e.id)), true);
        setSeleccion([]);
        return;
      }
      if (ev.key === 'Escape') {
        setSeleccion([]);
        return;
      }
      const paso = ev.shiftKey ? (10 / LIENZOS[formato].w) * 100 : (1 / LIENZOS[formato].w) * 100;
      const pasoY = ev.shiftKey ? (10 / LIENZOS[formato].h) * 100 : (1 / LIENZOS[formato].h) * 100;
      const flechas: Record<string, [number, number]> = {
        ArrowLeft: [-paso, 0],
        ArrowRight: [paso, 0],
        ArrowUp: [0, -pasoY],
        ArrowDown: [0, pasoY],
      };
      const delta = flechas[ev.key];
      if (delta) {
        ev.preventDefault();
        cambiarElementos(
          elementos.map((e) =>
            idsSeleccion.has(e.id)
              ? ({ ...e, rect: { ...e.rect, x: e.rect.x + delta[0], y: e.rect.y + delta[1] } } as PosterElement)
              : e,
          ),
          true,
        );
      }
    }
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  });

  const guardar = useMutation({
    mutationFn: () =>
      api(`/admin/plantillas/${plantillaId}/lienzos/${formato}`, {
        metodo: 'PUT',
        cuerpo: { fondoColor, fondoAssetId, elementos },
      }),
    onSuccess: async () => {
      setMensaje('Guardado');
      setSucio(false);
      await qc.invalidateQueries({ queryKey: ['admin-plantilla', plantillaId] });
      setTimeout(() => setMensaje(null), 1800);
    },
    onError: (e) => setMensaje(`Error: ${e.message}`),
  });

  const publicar = useMutation({
    mutationFn: async () => {
      if (sucio) await guardar.mutateAsync();
      await api(`/admin/plantillas/${plantillaId}/publicar`, { metodo: 'POST' });
    },
    onSuccess: async () => {
      setMensaje('Publicada: visible para todos los clientes');
      await qc.invalidateQueries({ queryKey: ['admin-plantilla', plantillaId] });
    },
    onError: (e) => setMensaje(`Error: ${e.message}`),
  });

  const adaptar = useMutation({
    mutationFn: async (sobrescribir: boolean) => {
      if (sucio) await guardar.mutateAsync();
      const destinos = FORMATOS.filter((f) => f !== formato);
      await api(`/admin/plantillas/${plantillaId}/adaptar`, {
        metodo: 'POST',
        cuerpo: { origen: formato, destinos, sobrescribir },
      });
    },
    onSuccess: async () => {
      setMensaje('Adaptado a los demás formatos');
      await qc.invalidateQueries({ queryKey: ['admin-plantilla', plantillaId] });
    },
    onError: async (e: Error & { code?: string }) => {
      // D24: con diseño previo se pregunta antes de sobrescribir.
      if ((e as { code?: string }).code === 'CONFLICT') {
        if (window.confirm('Hay formatos con diseño previo. ¿Sobrescribirlos?')) {
          await adaptar.mutateAsync(true);
        }
      } else {
        setMensaje(`Error: ${e.message}`);
      }
    },
  });

  async function subirFondo() {
    const archivo = fondoInputRef.current?.files?.[0];
    if (!archivo) return;
    const forma = new FormData();
    forma.append('tipo', 'IMAGEN');
    forma.append('archivo', archivo);
    const asset = await apiMultipart<{ assetId: string; url: string }>('/admin/media', forma);
    setFondoAssetId(asset.assetId);
    setFondoUrl(asset.url);
    setSucio(true);
  }

  /** Sube un PNG/JPG y lo agrega como elemento `imagen` (o reemplaza el seleccionado). */
  async function subirImagenElemento() {
    const archivo = imagenInputRef.current?.files?.[0];
    if (!archivo) return;
    const forma = new FormData();
    forma.append('tipo', 'IMAGEN');
    forma.append('archivo', archivo);
    const asset = await apiMultipart<{ assetId: string; url: string; width: number | null; height: number | null }>(
      '/admin/media',
      forma,
    );
    setAssets((previos) => ({ ...previos, [asset.assetId]: asset.url }));
    if (imagenInputRef.current) imagenInputRef.current.value = '';

    const idReemplazo = reemplazarImagenDe.current;
    reemplazarImagenDe.current = null;
    if (idReemplazo) {
      cambiarElementos(
        elementos.map((e) => (e.id === idReemplazo && e.tipo === 'imagen' ? { ...e, assetId: asset.assetId } : e)),
        true,
      );
      return;
    }

    // Tamaño inicial: 40% del ancho, alto según la proporción real de la imagen.
    const dims = LIENZOS[formato];
    const razon = asset.width && asset.height ? asset.width / asset.height : 1;
    const w = 40; // % del ancho
    // alto en % del lienzo que conserva la proporción real de la imagen
    const h = Math.min(90, ((w / 100) * dims.w) / razon / dims.h * 100);
    const nuevo: PosterElement = {
      id: idNuevo('imagen'),
      nombre: archivo.name.replace(/\.[a-z0-9]+$/i, ''),
      rect: { x: 30, y: 30, w, h },
      rotacion: 0,
      z: elementos.length + 1,
      opacidad: 1,
      visible: true,
      opcional: false,
      anclaje: ANCLA_DEFECTO,
      bloqueado: false,
      restricciones: { ...RESTRICCIONES_DEFECTO, proporcionFija: true },
      tipo: 'imagen',
      assetId: asset.assetId,
      ajuste: 'contain',
      radio: 0,
    };
    cambiarElementos([...elementos, nuevo], true);
    setSeleccion([nuevo.id]);
  }

  const svgInputRef = useRef<HTMLInputElement>(null);

  /**
   * Importa un SVG partiéndolo en CAPAS independientes: cada hijo directo del
   * <svg> (grupo o figura) se vuelve un elemento `vector` con su propia caja y
   * color editable. Misma validación de seguridad que el servidor.
   */
  function importarSvgCapas() {
    const archivo = svgInputRef.current?.files?.[0];
    if (svgInputRef.current) svgInputRef.current.value = '';
    if (!archivo) return;
    void archivo.text().then((texto) => {
      if (/<\s*script|<\s*foreignObject|<\s*(iframe|embed|object)\b|\son[a-z]+\s*=|javascript\s*:/i.test(texto)) {
        setMensaje('Error: SVG rechazado (contiene scripts o contenido activo)');
        return;
      }
      const doc = new DOMParser().parseFromString(texto, 'image/svg+xml');
      const raiz = doc.querySelector('svg');
      if (!raiz || doc.querySelector('parsererror')) {
        setMensaje('Error: el archivo no es un SVG válido');
        return;
      }

      // Montado oculto: getBBox() sólo funciona con el SVG en el documento.
      const escondite = document.createElement('div');
      escondite.style.cssText = 'position:absolute;left:-99999px;top:0;width:1000px;height:1000px;';
      document.body.appendChild(escondite);
      escondite.appendChild(raiz);

      try {
        const FIGURAS = new Set(['g', 'path', 'rect', 'circle', 'ellipse', 'polygon', 'polyline', 'line', 'text', 'use']);
        const capas = [...raiz.children].filter((n) => FIGURAS.has(n.tagName.toLowerCase()));
        if (capas.length === 0) {
          setMensaje('Error: el SVG no tiene figuras en el nivel superior');
          return;
        }
        if (capas.length > 60) {
          setMensaje('Error: el SVG tiene más de 60 capas; agrupe figuras en su editor de vectores');
          return;
        }

        // Encuadre: el arte completo entra centrado ocupando ~70% del ancho del lienzo.
        const lz = LIENZOS[formato];
        const vb = (raiz as SVGSVGElement).viewBox.baseVal;
        const total = (raiz as SVGGraphicsElement).getBBox();
        const marco = vb && vb.width > 0 ? vb : total;
        const escalaPx = (0.7 * lz.w) / marco.width;
        const offX = 0.15 * lz.w - marco.x * escalaPx;
        const offY = (lz.h - marco.height * escalaPx) / 2 - marco.y * escalaPx;

        const nuevos: PosterElement[] = [];
        capas.forEach((capa, i) => {
          const bb = (capa as SVGGraphicsElement).getBBox();
          if (bb.width === 0 || bb.height === 0) return;
          nuevos.push({
            id: idNuevo('vector'),
            nombre: capa.getAttribute('id') || `${archivo.name.replace(/\.svg$/i, '')} · capa ${i + 1}`,
            rect: {
              x: ((offX + bb.x * escalaPx) / lz.w) * 100,
              y: ((offY + bb.y * escalaPx) / lz.h) * 100,
              w: Math.max(((bb.width * escalaPx) / lz.w) * 100, 0.5),
              h: Math.max(((bb.height * escalaPx) / lz.h) * 100, 0.5),
            },
            rotacion: 0,
            z: elementos.length + i + 1,
            opacidad: 1,
            visible: true,
      opcional: false,
            anclaje: ANCLA_DEFECTO,
            bloqueado: false,
            restricciones: { ...RESTRICCIONES_DEFECTO, proporcionFija: true, dentroDelLienzo: false, tamanoMin: 0.5 },
            tipo: 'vector',
            svg: capa.outerHTML,
            viewBox: `${bb.x} ${bb.y} ${bb.width} ${bb.height}`,
            color: null,
          });
        });
        if (nuevos.length === 0) {
          setMensaje('Error: no se encontraron figuras con área en el SVG');
          return;
        }
        cambiarElementos([...elementos, ...nuevos], true);
        setSeleccion(nuevos.map((n) => n.id));
        setMensaje(`SVG importado: ${nuevos.length} capa(s) independientes.`);
      } finally {
        document.body.removeChild(escondite);
      }
    });
  }

  function agregarElemento(tipo: string) {
    const base = {
      id: idNuevo(tipo),
      nombre: tipo,
      rect: { x: 30, y: 40, w: 40, h: 12 },
      rotacion: 0,
      z: elementos.length + 1,
      opacidad: 1,
      visible: true,
      opcional: false,
      anclaje: ANCLA_DEFECTO,
      bloqueado: false,
      restricciones: { ...RESTRICCIONES_DEFECTO },
    };
    let nuevo: PosterElement;
    switch (tipo) {
      case 'texto':
        nuevo = {
          ...base, tipo: 'texto', nombre: 'Texto', contenido: 'Texto nuevo', binding: 'libre',
          fuenteId: fuenteDefecto, tamano: 56, peso: 700, italic: false, color: '#111225',
          align: 'center', interlineado: 1.15, transform: 'none', autoAjustar: true, maxLineas: 2,
        };
        break;
      case 'producto': {
        const usados = elementos.filter((e) => e.tipo === 'producto').length;
        if (usados >= (plantilla.data?.numProductos ?? 1)) {
          setMensaje(`La plantilla admite ${plantilla.data?.numProductos} espacio(s) de producto`);
          return;
        }
        nuevo = {
          ...base, tipo: 'producto', nombre: `Producto ${usados + 1}`, indice: usados as 0 | 1 | 2,
          ajuste: 'contain', radio: 16,
          rect: { x: 30, y: 35, w: 40, h: 30 },
          restricciones: { ...RESTRICCIONES_DEFECTO, proporcionFija: true },
        };
        break;
      }
      case 'precio': {
        const usados = elementos.filter((e) => e.tipo === 'precio').length;
        nuevo = {
          ...base, tipo: 'precio', nombre: `Precio ${usados + 1}`, indice: Math.min(usados, 2) as 0 | 1 | 2,
          estilo: 'antes_ahora', fuenteId: fuenteDefecto, tamanoAhora: 52, tamanoAntes: 28,
          colorAhora: '#e11d48', colorAntes: '#94a3b8', prefijo: '$', decimales: 2, radio: 8,
          rect: { x: 38, y: 70, w: 24, h: 9 },
        };
        break;
      }
      case 'logoMarca':
        nuevo = {
          ...base, tipo: 'logoMarca', nombre: 'Logo de marca', ajuste: 'contain', color: 'original',
          rect: { x: 40, y: 4, w: 20, h: 10 },
          restricciones: { ...RESTRICCIONES_DEFECTO, proporcionFija: true },
        };
        break;
      case 'logoCliente':
        nuevo = {
          ...base, tipo: 'logoCliente', nombre: 'Logo del negocio', ajuste: 'contain', radio: 12,
          rect: { x: 4, y: 86, w: 16, h: 9 },
          restricciones: { ...RESTRICCIONES_DEFECTO, proporcionFija: true },
        };
        break;
      case 'contactos':
        nuevo = {
          ...base, tipo: 'contactos', nombre: 'Contactos', canales: ['WHATSAPP', 'INSTAGRAM'],
          direccion: 'fila', fuenteId: fuenteDefecto, tamano: 28, color: '#334155',
          mostrarIconos: true, separador: '  ·  ', rect: { x: 22, y: 88, w: 72, h: 6 },
        };
        break;
      case 'forma':
        nuevo = {
          ...base, tipo: 'forma', nombre: 'Forma', forma: 'rect',
          relleno: { tipo: 'solido', color: '#e11d48' }, radio: 0,
        };
        break;
      default:
        return;
    }
    cambiarElementos([...elementos, nuevo], true);
    setSeleccion([nuevo.id]);
  }

  // ── Vista previa por marca: colores dinámicos y logo reales ───────────────
  const alternarMarca = useMutation({
    mutationFn: async (marcaId: string) => {
      const actuales = plantilla.data?.marcaIds ?? [];
      const nuevas = actuales.includes(marcaId)
        ? actuales.filter((m) => m !== marcaId)
        : [...actuales, marcaId];
      await api(`/admin/plantillas/${plantillaId}`, { metodo: 'PATCH', cuerpo: { marcaIds: nuevas } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-plantilla', plantillaId] }),
    onError: (e) => setMensaje(`Error: ${e.message}`),
  });

  const marcasVista = useQuery({
    queryKey: ['admin-marcas'],
    queryFn: () => api<{ items: MarcaVista[] }>('/admin/marcas'),
  });
  const [marcaPreviewId, setMarcaPreviewId] = useState<string | null>(null);
  const marcaPreview =
    marcasVista.data?.items.find((m) => m.id === marcaPreviewId) ?? marcasVista.data?.items[0] ?? null;
  const coloresPreview: ColoresMarca = {
    primario: marcaPreview?.colorPrimario ?? '#111225',
    secundario: marcaPreview?.colorSecundario ?? '#d7f75b',
  };
  const colorLogoMarca = (() => {
    const el = elementos.find((e) => e.tipo === 'logoMarca');
    return el?.tipo === 'logoMarca' ? el.color : 'original';
  })();
  const logoTintado = useQuery({
    queryKey: ['logo-marca-preview', marcaPreview?.id, colorLogoMarca],
    enabled: Boolean(marcaPreview) && elementos.some((e) => e.tipo === 'logoMarca'),
    queryFn: () =>
      api<{ url: string | null }>(`/catalogo/marcas/${marcaPreview!.id}/logo?color=${colorLogoMarca}`),
  });

  const resuelto = useMemo(
    () =>
      resueltoDeMuestra({
        formato,
        elementos: aplicarColoresMarca(elementos, coloresPreview),
        fondoColor: colorDeMarca(fondoColor, coloresPreview),
        fondoUrl,
        assets,
        logoMarcaUrl: logoTintado.data?.url ?? marcaPreview?.logoUrl ?? undefined,
        fuentes: (fuentes.data?.items ?? []).map((f) => ({
          fuenteId: f.id,
          familia: f.familia,
          url: f.url,
          peso: f.peso,
          italic: f.italic,
        })),
      }),
    [formato, elementos, fondoColor, fondoUrl, fuentes.data, assets, coloresPreview.primario, coloresPreview.secundario, logoTintado.data, marcaPreview?.logoUrl],
  );

  if (plantilla.isPending) return <p className="p-8 text-sm text-suave">Cargando…</p>;

  return (
    <main className="flex h-[calc(100vh-7.5rem)] flex-col">
      {/* Aviso en pantallas chicas: herramienta de diseño de escritorio (D37). */}
      <div className="block bg-amber-100 px-4 py-2 text-sm text-amber-800 min-[1024px]:hidden">
        El editor de plantillas está pensado para escritorio (pantallas de 1024 px o más).
      </div>

      <header className="tarjeta mb-3 flex flex-wrap items-center gap-2 px-4 py-2.5">
        <h1 className="mr-3 text-[17px] font-medium">{plantilla.data?.nombre}</h1>
        {FORMATOS.map((f) => (
          <Chip
            key={f}
            activo={f === formato}
            onClick={() => {
              if (sucio && !window.confirm('Hay cambios sin guardar. ¿Cambiar de formato igual?')) return;
              setFormato(f);
            }}
          >
            {NOMBRES_FORMATO[f] ?? f.replace('R', '').replace('_', ':')}
          </Chip>
        ))}
        <Boton variante="contorno" tamano="sm" onClick={() => adaptar.mutate(false)}>Adaptar a los demás</Boton>
        <div className="ml-auto flex items-center gap-1.5">
          <BotonCirculo contorno className="h-10 w-10" aria-label="Alejar" onClick={() => setZoom((z) => Math.max(0.2, z - 0.1))}>
            <Minus size={16} />
          </BotonCirculo>
          <span className="w-12 text-center text-xs text-suave">{Math.round(zoom * 100)}%</span>
          <BotonCirculo contorno className="h-10 w-10" aria-label="Acercar" onClick={() => setZoom((z) => Math.min(1, z + 0.1))}>
            <Plus size={16} />
          </BotonCirculo>
          <BotonCirculo contorno className="h-10 w-10" aria-label="Deshacer" onClick={deshacer}>
            <Undo2 size={16} />
          </BotonCirculo>
          <BotonCirculo contorno className="h-10 w-10" aria-label="Rehacer" onClick={rehacerCambio}>
            <Redo2 size={16} />
          </BotonCirculo>

          {/* Alineación y distribución de la selección (§5.3). Con 1: contra el
              lienzo; con varios: entre sí. Distribuir exige 3+. */}
          <div className="interno ml-1 flex items-center gap-0.5 !rounded-full p-1">
            {(
              [
                ['izquierda', <AlignStartVertical key="i" size={15} />, 'Alinear a la izquierda'],
                ['centroH', <AlignCenterVertical key="ch" size={15} />, 'Centrar horizontal'],
                ['derecha', <AlignEndVertical key="d" size={15} />, 'Alinear a la derecha'],
                ['arriba', <AlignStartHorizontal key="a" size={15} />, 'Alinear arriba'],
                ['centroV', <AlignCenterHorizontal key="cv" size={15} />, 'Centrar vertical'],
                ['abajo', <AlignEndHorizontal key="b" size={15} />, 'Alinear abajo'],
              ] as const
            ).map(([eje, icono, titulo]) => (
              <button
                key={eje}
                type="button"
                title={titulo}
                aria-label={titulo}
                disabled={seleccion.length === 0}
                onClick={() => alinearSeleccion(eje)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-principal transition hover:bg-superficie disabled:opacity-35"
              >
                {icono}
              </button>
            ))}
            <span className="mx-0.5 h-4 w-px bg-superficie" />
            <button
              type="button"
              title="Distribuir horizontal (3+)"
              aria-label="Distribuir horizontal"
              disabled={seleccion.length < 3}
              onClick={() => distribuirSeleccion('horizontal')}
              className="flex h-8 w-8 items-center justify-center rounded-full text-principal transition hover:bg-superficie disabled:opacity-35"
            >
              <AlignHorizontalDistributeCenter size={15} />
            </button>
            <button
              type="button"
              title="Distribuir vertical (3+)"
              aria-label="Distribuir vertical"
              disabled={seleccion.length < 3}
              onClick={() => distribuirSeleccion('vertical')}
              className="flex h-8 w-8 items-center justify-center rounded-full text-principal transition hover:bg-superficie disabled:opacity-35"
            >
              <AlignVerticalDistributeCenter size={15} />
            </button>
          </div>
          <Boton tamano="sm" variante="contorno" onClick={() => guardar.mutate()} disabled={guardar.isPending || !sucio}>
            {sucio ? 'Guardar' : 'Guardado'}
          </Boton>
          <Boton tamano="sm" variante="acento" onClick={() => publicar.mutate()} disabled={publicar.isPending}>Publicar</Boton>
        </div>
      </header>

      {mensaje ? (
        <div className="px-4 pt-2">
          <Aviso tono={mensaje.startsWith('Error') ? 'error' : 'ok'}>{mensaje}</Aviso>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 gap-3">
        {/* Paleta + capas */}
        <aside className="tarjeta w-60 shrink-0 space-y-5 overflow-y-auto p-4">
          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">Agregar</h2>
            <div className="grid grid-cols-2 gap-1.5">
              {[
                ['texto', 'Texto'],
                ['producto', 'Producto'],
                ['precio', 'Precio'],
                ['logoCliente', 'Logo negocio'],
                ['logoMarca', 'Logo marca'],
                ['contactos', 'Contactos'],
                ['forma', 'Forma'],
                ['imagen', 'Imagen PNG'],
                ['svgCapas', 'Importar SVG'],
              ].map(([tipo, etiqueta]) => (
                <button
                  key={tipo}
                  type="button"
                  onClick={() => {
                    if (tipo === 'imagen') {
                      reemplazarImagenDe.current = null;
                      imagenInputRef.current?.click();
                    } else if (tipo === 'svgCapas') {
                      svgInputRef.current?.click();
                    } else agregarElemento(tipo!);
                  }}
                  className="interno min-h-9 text-xs font-medium transition hover:opacity-80"
                >
                  {etiqueta}
                </button>
              ))}
            </div>
          </section>

          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">Marcas habilitadas</h2>
            <div className="flex flex-wrap gap-1.5">
              {marcasVista.data?.items.map((m) => (
                <Chip
                  key={m.id}
                  activo={(plantilla.data?.marcaIds ?? []).includes(m.id)}
                  onClick={() => alternarMarca.mutate(m.id)}
                >
                  {m.nombre}
                </Chip>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-suave">Sin ninguna marcada, la plantilla sirve para todas.</p>
          </section>

          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">Vista previa con</h2>
            <div className="flex flex-wrap gap-1.5">
              {marcasVista.data?.items.map((m) => (
                <Chip key={m.id} activo={marcaPreview?.id === m.id} onClick={() => setMarcaPreviewId(m.id)}>
                  {m.nombre}
                </Chip>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-suave">El logo y los colores de marca cambian según la marca del arte.</p>
          </section>

          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">Fondo</h2>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={fondoColor ?? '#ffffff'}
                onChange={(e) => {
                  setFondoColor(e.target.value);
                  setSucio(true);
                }}
                className="campo h-9 w-12"
              />
              <input ref={fondoInputRef} type="file" accept="image/*" className="hidden" onChange={() => void subirFondo()} />
              <input ref={svgInputRef} type="file" accept=".svg,image/svg+xml" className="hidden" onChange={() => importarSvgCapas()} />
              <input ref={imagenInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={() => void subirImagenElemento()} />
              <Boton variante="contorno" tamano="sm" onClick={() => fondoInputRef.current?.click()}>Imagen…</Boton>
            </div>
            {fondoUrl ? (
              <button
                type="button"
                className="mt-1 text-xs text-rose-600 underline"
                onClick={() => {
                  setFondoAssetId(null);
                  setFondoUrl(null);
                  setSucio(true);
                }}
              >
                Quitar imagen de fondo
              </button>
            ) : null}
          </section>

          <section>
            <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">Capas</h2>
            <ul className="space-y-1">
              {[...elementos]
                .sort((a, b) => b.z - a.z)
                .map((e) => (
                  <li key={e.id}>
                    <div
                      className={`flex items-center gap-1 rounded-xl px-2 py-1.5 text-xs ${
                        seleccion.includes(e.id) ? 'bg-negro text-white' : 'hover:bg-superficie2'
                      }`}
                    >
                      <button
                        type="button"
                        className="flex flex-1 items-center gap-1 truncate text-left"
                        onClick={(ev) =>
                          setSeleccion((previa) =>
                            ev.shiftKey
                              ? previa.includes(e.id)
                                ? previa.filter((x) => x !== e.id)
                                : [...previa, e.id]
                              : [e.id],
                          )
                        }
                      >
                        <span className="truncate">{e.nombre}</span>
                        {e.bloqueado ? <Lock size={12} className="flex-none text-suave" /> : null}
                      </button>
                      <button
                        type="button"
                        title="Subir"
                        aria-label="Subir capa"
                        onClick={() =>
                          cambiarElementos(elementos.map((x) => (x.id === e.id ? { ...x, z: x.z + 1 } : x)) as PosterElement[], true)
                        }
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        title="Bajar"
                        aria-label="Bajar capa"
                        onClick={() =>
                          cambiarElementos(elementos.map((x) => (x.id === e.id ? { ...x, z: x.z - 1 } : x)) as PosterElement[], true)
                        }
                      >
                        <ChevronDown size={14} />
                      </button>
                      <button
                        type="button"
                        title="Mostrar/ocultar"
                        aria-label="Mostrar u ocultar"
                        onClick={() =>
                          cambiarElementos(elementos.map((x) => (x.id === e.id ? { ...x, visible: !x.visible } : x)) as PosterElement[], true)
                        }
                      >
                        {e.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                      </button>
                    </div>
                  </li>
                ))}
            </ul>
          </section>
        </aside>

        {/* Lienzo */}
        <section className="flex min-w-0 flex-1 items-start justify-center overflow-auto rounded-[28px] bg-superficie2 p-6">
          <LienzoEditor
            resuelto={resuelto}
            elementos={elementos}
            pintarElementos={(els) => aplicarColoresMarca(els, coloresPreview)}
            onCambiar={cambiarEnVivo}
            onCommit={apilarHistorial}
            seleccion={seleccion}
            onSeleccion={setSeleccion}
            escala={zoom}
            modo="empresa"
          />
        </section>

        {/* Propiedades */}
        <aside className="tarjeta w-[340px] shrink-0 overflow-y-auto p-4">
          {elemento ? (
            <PanelPropiedades
              elemento={elemento}
              lienzo={LIENZOS[formato]}
              fuentes={fuentes.data?.items ?? []}
              coloresMarca={coloresPreview}
              onReemplazarImagen={() => {
                reemplazarImagenDe.current = elemento.id;
                imagenInputRef.current?.click();
              }}
              onCambiar={(cambio) =>
                cambiarElementos(
                  elementos.map((e) => (e.id === elemento.id ? ({ ...e, ...cambio } as PosterElement) : e)),
                  true,
                )
              }
            />
          ) : (
            <p className="p-2 text-xs text-suave">
              Seleccione un elemento para editar sus propiedades y permisos.
            </p>
          )}
        </aside>
      </div>
    </main>
  );
}

function Fila({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <label className="mb-2 block text-xs">
      <span className="mb-0.5 block font-medium text-suave">{etiqueta}</span>
      {children}
    </label>
  );
}

const claseInput = 'campo campo-compacto min-h-9 rounded-xl text-xs';

function PanelPropiedades({
  elemento,
  lienzo,
  fuentes,
  coloresMarca,
  onCambiar,
  onReemplazarImagen,
}: {
  elemento: PosterElement;
  lienzo: { w: number; h: number };
  fuentes: Fuente[];
  coloresMarca: ColoresMarca;
  onCambiar: (cambio: Partial<PosterElement>) => void;
  onReemplazarImagen: () => void;
}) {
  // ── Posición y tamaño en PÍXELES del lienzo (1080 × alto del formato) ──
  const px = {
    x: Math.round((elemento.rect.x / 100) * lienzo.w),
    y: Math.round((elemento.rect.y / 100) * lienzo.h),
    w: Math.round((elemento.rect.w / 100) * lienzo.w),
    h: Math.round((elemento.rect.h / 100) * lienzo.h),
  };
  const razonPx = px.h > 0 ? px.w / px.h : 1;
  const ligado = elemento.tipo === 'producto' || elemento.restricciones.proporcionFija;

  function fijarPx(campo: 'x' | 'y' | 'w' | 'h', valor: number) {
    if (!Number.isFinite(valor)) return;
    const rect = { ...elemento.rect };
    if (campo === 'x') rect.x = (valor / lienzo.w) * 100;
    if (campo === 'y') rect.y = (valor / lienzo.h) * 100;
    if (campo === 'w') {
      const w = Math.max(1, valor);
      rect.w = (w / lienzo.w) * 100;
      // producto: siempre cuadrado; proporción fija: el alto acompaña
      const h = elemento.tipo === 'producto' ? w : ligado ? w / razonPx : px.h;
      rect.h = (h / lienzo.h) * 100;
    }
    if (campo === 'h') {
      const h = Math.max(1, valor);
      rect.h = (h / lienzo.h) * 100;
      const w = elemento.tipo === 'producto' ? h : ligado ? h * razonPx : px.w;
      rect.w = (w / lienzo.w) * 100;
    }
    onCambiar({ rect });
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">
          {elemento.tipo}
        </h2>
        <Fila etiqueta="Nombre visible para el cliente">
          <input
            className={claseInput}
            value={elemento.nombre}
            onChange={(e) => onCambiar({ nombre: e.target.value })}
          />
        </Fila>

        {/* Medidas exactas: escribir 300 y el elemento mide 300 px en el PNG final */}
        <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-suave">
          Posición y tamaño (px · lienzo {lienzo.w}×{lienzo.h})
        </p>
        <div className="grid grid-cols-4 gap-2">
          {(
            [
              ['x', 'X'],
              ['y', 'Y'],
              ['w', 'Ancho'],
              ['h', 'Alto'],
            ] as const
          ).map(([campo, etiqueta]) => (
            <Fila key={campo} etiqueta={etiqueta}>
              <input
                key={`${campo}-${px[campo]}`}
                type="number"
                className={claseInput}
                defaultValue={px[campo]}
                onBlur={(e) => fijarPx(campo, Number(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fijarPx(campo, Number((e.target as HTMLInputElement).value));
                }}
              />
            </Fila>
          ))}
        </div>
        {ligado ? (
          <p className="-mt-1 mb-2 text-[10px] text-suave">
            {elemento.tipo === 'producto' ? 'Producto: siempre cuadrado (D21).' : 'Proporción fija: ancho y alto van ligados.'}
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2">
          <Fila etiqueta="Rotación">
            <input
              type="number"
              className={claseInput}
              value={elemento.rotacion}
              onChange={(e) => onCambiar({ rotacion: Number(e.target.value) })}
            />
          </Fila>
          <Fila etiqueta="Opacidad">
            <input
              type="number"
              step="0.05"
              min="0"
              max="1"
              className={claseInput}
              value={elemento.opacidad}
              onChange={(e) => onCambiar({ opacidad: Number(e.target.value) })}
            />
          </Fila>
        </div>
      </div>

      {elemento.tipo === 'texto' ? (
        <div>
          <Fila etiqueta="Contenido">
            <textarea
              className={claseInput}
              rows={2}
              value={elemento.contenido}
              onChange={(e) => onCambiar({ contenido: e.target.value } as Partial<PosterElement>)}
            />
          </Fila>
          <div className="grid grid-cols-2 gap-2">
            <Fila etiqueta="Binding">
              <select
                className={claseInput}
                value={elemento.binding}
                onChange={(e) => onCambiar({ binding: e.target.value } as never)}
              >
                {['libre', 'titulo', 'subtitulo', 'insignia', 'nombreNegocio'].map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </Fila>
            <div className="col-span-2 mb-2 block text-xs">
              <span className="mb-0.5 block font-medium text-suave">Tipografía</span>
              <SelectorFuente
                compacto
                fuentes={fuentes}
                valor={elemento.fuenteId}
                peso={elemento.peso}
                onElegir={(f) => onCambiar({ fuenteId: f.id, peso: f.peso } as never)}
              />
            </div>
            <Fila etiqueta="Tamaño (px)">
              <input
                type="number"
                className={claseInput}
                value={elemento.tamano}
                onChange={(e) => onCambiar({ tamano: Number(e.target.value) } as never)}
              />
            </Fila>
            <Fila etiqueta="Itálica">
              <select
                className={claseInput}
                value={elemento.italic ? 'si' : 'no'}
                onChange={(e) => onCambiar({ italic: e.target.value === 'si' } as never)}
              >
                <option value="no">Normal</option>
                <option value="si">Itálica</option>
              </select>
            </Fila>
            <Fila etiqueta="Color">
              <CampoColorHex valor={elemento.color} coloresMarca={coloresMarca} onCambiar={(v) => onCambiar({ color: v } as never)} />
            </Fila>
            <Fila etiqueta="Color de la marca">
              <ChipsColorMarca valor={elemento.color} onCambiar={(v) => onCambiar({ color: v } as never)} />
            </Fila>
            <Fila etiqueta="Alineación">
              <select
                className={claseInput}
                value={elemento.align}
                onChange={(e) => onCambiar({ align: e.target.value } as never)}
              >
                <option value="left">Izquierda</option>
                <option value="center">Centro</option>
                <option value="right">Derecha</option>
              </select>
            </Fila>
            <Fila etiqueta="Máx. líneas">
              <input
                type="number"
                min="1"
                max="10"
                className={claseInput}
                value={elemento.maxLineas}
                onChange={(e) => onCambiar({ maxLineas: Number(e.target.value) } as never)}
              />
            </Fila>
            <div className="col-span-2">
              <Casilla
                activa={elemento.autoAjustar}
                onCambiar={(v) => onCambiar({ autoAjustar: v } as never)}
                etiqueta={<span className="text-xs">Auto-ajustar al recuadro (el texto nunca supera la caja)</span>}
              />
            </div>
            <Fila etiqueta="Transformación">
              <select
                className={claseInput}
                value={elemento.transform}
                onChange={(e) => onCambiar({ transform: e.target.value } as never)}
              >
                <option value="none">Normal</option>
                <option value="upper">MAYÚSCULAS</option>
                <option value="lower">minúsculas</option>
              </select>
            </Fila>
          </div>
        </div>
      ) : null}

      {elemento.tipo === 'precio' ? (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 mb-2 block text-xs">
            <span className="mb-0.5 block font-medium text-suave">Tipografía</span>
            <SelectorFuente
              compacto
              fuentes={fuentes}
              valor={elemento.fuenteId}
              onElegir={(f) => onCambiar({ fuenteId: f.id } as never)}
            />
          </div>
          <Fila etiqueta="Estilo">
            <select
              className={claseInput}
              value={elemento.estilo}
              onChange={(e) => onCambiar({ estilo: e.target.value } as never)}
            >
              <option value="simple">Simple</option>
              <option value="antes_ahora">Antes / Ahora</option>
            </select>
          </Fila>
          <Fila etiqueta="Prefijo">
            <input
              className={claseInput}
              value={elemento.prefijo}
              onChange={(e) => onCambiar({ prefijo: e.target.value } as never)}
            />
          </Fila>
          <Fila etiqueta="Tamaño Ahora">
            <input
              type="number"
              className={claseInput}
              value={elemento.tamanoAhora}
              onChange={(e) => onCambiar({ tamanoAhora: Number(e.target.value) } as never)}
            />
          </Fila>
          <Fila etiqueta="Color Ahora">
            <CampoColorHex valor={elemento.colorAhora} coloresMarca={coloresMarca} onCambiar={(v) => onCambiar({ colorAhora: v } as never)} />
          </Fila>
          <Fila etiqueta="Color de la marca">
            <ChipsColorMarca valor={elemento.colorAhora} onCambiar={(v) => onCambiar({ colorAhora: v } as never)} />
          </Fila>
          <Fila etiqueta="Decimales">
            <select
              className={claseInput}
              value={elemento.decimales}
              onChange={(e) => onCambiar({ decimales: Number(e.target.value) } as never)}
            >
              <option value="2">2</option>
              <option value="0">0</option>
            </select>
          </Fila>
        </div>
      ) : null}

      {elemento.tipo === 'vector' ? (
        <div className="grid grid-cols-2 gap-2">
          <Fila etiqueta="Color">
            <CampoColorHex
              valor={elemento.color ?? '#111225'}
              coloresMarca={coloresMarca}
              onCambiar={(v) => onCambiar({ color: v } as never)}
            />
          </Fila>
          <Fila etiqueta="Color de la marca">
            <ChipsColorMarca valor={elemento.color ?? ''} onCambiar={(v) => onCambiar({ color: v } as never)} />
          </Fila>
          {elemento.color !== null ? (
            <div className="col-span-2">
              <Boton variante="texto" tamano="sm" type="button" onClick={() => onCambiar({ color: null } as never)}>
                Volver a los colores originales del SVG
              </Boton>
            </div>
          ) : (
            <p className="col-span-2 text-[11px] text-suave">Colores originales del SVG.</p>
          )}
        </div>
      ) : null}

      {elemento.tipo === 'logoMarca' ? (
        <div className="grid grid-cols-1 gap-2">
          <Fila etiqueta="Color del logo">
            <select
              className={claseInput}
              value={elemento.color}
              onChange={(e) => onCambiar({ color: e.target.value } as never)}
            >
              <option value="original">Original</option>
              <option value="primario">Color principal de la marca</option>
              <option value="secundario">Color secundario de la marca</option>
              <option value="blanco">Blanco</option>
              <option value="negro">Negro</option>
            </select>
          </Fila>
          <p className="text-[11px] text-suave">El tinte necesita que el logo de la marca sea SVG.</p>
        </div>
      ) : null}

      {elemento.tipo === 'imagen' ? (
        <div className="grid grid-cols-2 gap-2">
          <Fila etiqueta="Ajuste">
            <select
              className={claseInput}
              value={elemento.ajuste}
              onChange={(e) => onCambiar({ ajuste: e.target.value } as never)}
            >
              <option value="contain">Completa (contain)</option>
              <option value="cover">Rellenar (cover)</option>
            </select>
          </Fila>
          <Fila etiqueta="Radio">
            <input
              type="number"
              className={claseInput}
              value={elemento.radio}
              onChange={(e) => onCambiar({ radio: Number(e.target.value) } as never)}
            />
          </Fila>
          <div className="col-span-2">
            <Boton variante="contorno" tamano="sm" className="w-full" onClick={onReemplazarImagen}>
              Reemplazar imagen
            </Boton>
          </div>
        </div>
      ) : null}

      {elemento.tipo === 'forma' ? (
        <div className="grid grid-cols-2 gap-2">
          <Fila etiqueta="Forma">
            <select
              className={claseInput}
              value={elemento.forma}
              onChange={(e) => onCambiar({ forma: e.target.value } as never)}
            >
              <option value="rect">Rectángulo</option>
              <option value="circulo">Círculo</option>
              <option value="linea">Línea</option>
            </select>
          </Fila>
          <Fila etiqueta="Radio">
            <input
              type="number"
              className={claseInput}
              value={elemento.radio}
              onChange={(e) => onCambiar({ radio: Number(e.target.value) } as never)}
            />
          </Fila>
          <Fila etiqueta="Color">
            <CampoColorHex
              valor={elemento.relleno.tipo === 'solido' ? elemento.relleno.color : '#000000'}
              coloresMarca={coloresMarca}
              onCambiar={(v) => onCambiar({ relleno: { tipo: 'solido', color: v } } as never)}
            />
          </Fila>
          <Fila etiqueta="Color de la marca">
            <ChipsColorMarca
              valor={elemento.relleno.tipo === 'solido' ? elemento.relleno.color : '#000000'}
              onCambiar={(v) => onCambiar({ relleno: { tipo: 'solido', color: v } } as never)}
            />
          </Fila>
        </div>
      ) : null}

      {/* Permisos: panel simple por elemento (§5.5). */}
      <div>
        <h2 className="mb-2 text-[11px] font-medium uppercase tracking-wide text-suave">
          Permisos del cliente
        </h2>
        <Casilla
          activa={elemento.bloqueado}
          onCambiar={(v) => onCambiar({ bloqueado: v })}
          etiqueta={<span className="text-xs">Bloqueado (intocable, p. ej. logo de marca)</span>}
        />
        <Casilla
          activa={elemento.opcional}
          onCambiar={(v) => onCambiar({ opcional: v })}
          etiqueta={<span className="text-xs">Opcional (el cliente puede ponerlo o quitarlo)</span>}
        />
        <Casilla
          activa={elemento.restricciones.dentroDelLienzo}
          onCambiar={(v) =>
            onCambiar({ restricciones: { ...elemento.restricciones, dentroDelLienzo: v } } as never)
          }
          etiqueta="Mantenerlo dentro del lienzo (el cliente no puede sacarlo)"
        />
        <Casilla
          activa={elemento.restricciones.proporcionFija}
          onCambiar={(v) => onCambiar({ restricciones: { ...elemento.restricciones, proporcionFija: v } })}
          etiqueta={<span className="text-xs">Proporción fija (sin handles laterales)</span>}
        />
        <div className="grid grid-cols-2 gap-2">
          <Fila etiqueta="Tamaño mín. (%)">
            <input
              type="number"
              className={claseInput}
              value={elemento.restricciones.tamanoMin}
              onChange={(e) =>
                onCambiar({ restricciones: { ...elemento.restricciones, tamanoMin: Number(e.target.value) } })
              }
            />
          </Fila>
          <Fila etiqueta="Tamaño máx. (%)">
            <input
              type="number"
              className={claseInput}
              value={elemento.restricciones.tamanoMax}
              onChange={(e) =>
                onCambiar({ restricciones: { ...elemento.restricciones, tamanoMax: Number(e.target.value) } })
              }
            />
          </Fila>
        </div>
      </div>
    </div>
  );
}




/** Círculo de color + campo hex; con token de marca el círculo muestra el color de la vista previa. */
function CampoColorHex({
  valor,
  coloresMarca,
  onCambiar,
}: {
  valor: string;
  coloresMarca: ColoresMarca;
  onCambiar: (v: string) => void;
}) {
  const esPrimario = valor === COLOR_MARCA_PRIMARIO;
  const esSecundario = valor === COLOR_MARCA_SECUNDARIO;
  const esToken = esPrimario || esSecundario;
  const efectivo = esPrimario ? coloresMarca.primario : esSecundario ? coloresMarca.secundario : valor;
  const [texto, setTexto] = useState(esToken ? '' : valor);
  useEffect(() => {
    setTexto(esToken ? '' : valor);
  }, [valor, esToken]);

  function aplicarHex(v: string) {
    const limpio = `#${v.trim().replace(/^#/, '')}`;
    if (/^#[0-9a-fA-F]{6}$/.test(limpio)) onCambiar(limpio.toLowerCase());
  }

  return (
    <div className="campo campo-compacto flex items-center gap-2">
      <span
        className="relative inline-block h-6 w-6 shrink-0 overflow-hidden rounded-full border border-black/10 shadow-inner"
        style={{ backgroundColor: efectivo }}
      >
        <input
          type="color"
          value={/^#[0-9a-fA-F]{6}$/.test(efectivo) ? efectivo : '#000000'}
          onChange={(e) => onCambiar(e.target.value)}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label="Elegir color"
        />
      </span>
      <input
        value={texto}
        placeholder={esPrimario ? '1º de la marca' : esSecundario ? '2º de la marca' : '#000000'}
        onChange={(e) => {
          setTexto(e.target.value);
          aplicarHex(e.target.value);
        }}
        className="w-full bg-transparent font-mono text-xs uppercase outline-none placeholder:normal-case"
        maxLength={7}
        aria-label="Color en hex"
      />
    </div>
  );
}

/** Chips 1º/2º: el elemento usa el color DINÁMICO de la marca (se guarda el token). */
function ChipsColorMarca({ valor, onCambiar }: { valor: string; onCambiar: (v: string) => void }) {
  const esPrimario = valor === COLOR_MARCA_PRIMARIO;
  const esSecundario = valor === COLOR_MARCA_SECUNDARIO;
  // Misma altura y radio píldora que .campo, para que la fila quede pareja.
  const chip = (activo: boolean) =>
    `min-h-11 flex-1 rounded-full text-[13px] font-semibold transition ${activo ? 'bg-negro text-white' : 'bg-superficie2 text-principal hover:opacity-80'}`;
  return (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        title="Usar el color principal de la marca"
        className={chip(esPrimario)}
        onClick={() => onCambiar(esPrimario ? '#111225' : COLOR_MARCA_PRIMARIO)}
      >
        1º
      </button>
      <button
        type="button"
        title="Usar el color secundario de la marca"
        className={chip(esSecundario)}
        onClick={() => onCambiar(esSecundario ? '#111225' : COLOR_MARCA_SECUNDARIO)}
      >
        2º
      </button>
    </div>
  );
}
